import { skapi } from "@/main";
import { customer, user } from "@/code/user";
import { ref } from "vue";
import { resolveRegion } from "@/code/regions";
import { product_price_code } from "@/code/service";
import { showToast } from "@/components/toast";
import { markLeavingFor } from "@/code/back-forward";

// The payment provider keeps at most 500 characters in one metadata value
// (CHECKOUT_DESCRIPTION_MAX in views/new-project.ts is the same figure).
const METADATA_VALUE_MAX = 500;

// The project id written into the subscription's description, in the compact form
// the dashboard shows as the Project ID (<base62>-<base62>). Subscriptions bought
// earlier keep the longer <id>-<region><id>-<owner uuid> form in their description:
// it names the same project, and billing reads the metadata, never this text.
const projectIdOf = (serviceId: string, ownerId: string): string =>
    skapi.util.formatServiceId(serviceId, ownerId);

export const runningMessage = ref("Loading Service...");
export async function createSubscription(params: {
    ticket_id: string;
    service_info?: any;
    is_perpetual?: boolean;
    /**
     * A project that does not exist yet (views/new-project.ts). Its id is
     * reserved here, the billing ticket creates it once the payment succeeds,
     * and the success page waits for it (service/main.vue, /my-projects/await-<id>).
     * `spec` is the kind of project to create, as /create-service takes it.
     */
    new_project?: { name: string; description: string; region: string; spec?: string };
    /** Called with the id reserved for a new project, before the checkout is opened. */
    onReserved?: (serviceId: string) => void;
    onBeforeRedirect?: () => void;
}) {
    const {
        ticket_id,
        service_info = null,
        is_perpetual = false,
        new_project = null,
        onReserved,
        onBeforeRedirect,
    } = params;

    let resolvedCustomer = await customer;
    let product = product_price_code;
    let customer_id = resolvedCustomer.id;
    let currentUrl = window.location;

    runningMessage.value = "Creating checkout session...";

    let response;

    if (service_info === null) {
        if (!new_project?.name) throw new Error("A new project needs a name.");
        let endpoints = await Promise.all([
            skapi.admin_endpoint,
            skapi.record_endpoint,
        ]);
        let admin_private_endpoint = endpoints[0].admin_private; // https://.../

        let service = await skapi.util.request(
            admin_private_endpoint + "register-service",
            {
                service: skapi.service,
                owner: skapi.owner,
                execute: "genid",
                // the region is the id's prefix, so it is chosen here, for good
                region: resolveRegion(new_project.region, skapi.connection?.locale),
            },
            { auth: true },
        );

        if (onReserved) onReserved(service.service);

        let data: { [key: string]: any } = {
            client_reference_id: user.user_id,
            customer: customer_id,
            "customer_update[name]": "auto",
            "customer_update[address]": "auto",

            // back to the plan page, which still holds the details
            cancel_url: currentUrl.origin + "/create",
            "line_items[0][quantity]": 1,
            "line_items[0][price]": product[ticket_id],
            success_url:
                currentUrl.origin + "/my-projects/await-" + service.service,
            "tax_id_collection[enabled]": true,
        };

        Object.assign(data, {
            "subscription_data[metadata][service]": service.service,
            "subscription_data[metadata][owner]": service.owner,
            // allow_promotion_codes: true,
            // "discounts[0][promotion_code]": product["50-percent-discount"],
            // A new project is charged at checkout: no trial period.
            mode: "subscription",
            payment_method_collection: "always",
            "subscription_data[description]":
                'Subscription plan for project ID: "' +
                projectIdOf(service.service, service.owner) +
                '"',
            "subscription_data[metadata][name_to_create]": new_project.name,
            "subscription_data[metadata][username]": user.user_id,
        });
        if (new_project.description) {
            data["subscription_data[metadata][description]"] = new_project.description.slice(0, METADATA_VALUE_MAX);
        }
        if (new_project.spec) {
            data["subscription_data[metadata][spec]"] = new_project.spec;
        }

        response = await skapi.forwardRequest(null, {
            secretName: "stripe_test",
            url: "https://api.stripe.com/v1/checkout/sessions",
            method: "POST",
            headers: {
                Authorization: "Bearer $CLIENT_SECRET",
                "Content-Type": "application/x-www-form-urlencoded",
            },
            data,
        });
    } else {
        let data = {
            client_reference_id: service_info.owner,
            customer: customer_id,
            "customer_update[name]": "auto",
            "customer_update[address]": "auto",

            cancel_url: currentUrl.origin + currentUrl.pathname,
            "line_items[0][quantity]": 1,
            "line_items[0][price]": product[ticket_id],
            success_url: currentUrl.origin + "/my-projects/" + service_info.id,
            "tax_id_collection[enabled]": true,
        };

        if (is_perpetual) {
            Object.assign(data, {
                "metadata[service]": service_info.id,
                "metadata[owner]": service_info.owner,
                "metadata[price]": product[ticket_id],
                mode: "payment",
            });
        } else {
            // Every plan is charged at checkout: no trial period.
            Object.assign(data, {
                "subscription_data[metadata][service]": service_info.id,
                "subscription_data[metadata][owner]": service_info.owner,
                // allow_promotion_codes: true,
                // "discounts[0][promotion_code]": product["50-percent-discount"],
                mode: "subscription",
                "subscription_data[description]":
                    'Subscription plan for project ID: "' +
                    projectIdOf(service_info.id, service_info.owner) +
                    '"',
            });
        }

        response = await skapi.forwardRequest(null, {
            secretName: "stripe_test",
            url: "https://api.stripe.com/v1/checkout/sessions",
            method: "POST",
            headers: {
                Authorization: "Bearer $CLIENT_SECRET",
                "Content-Type": "application/x-www-form-urlencoded",
            },
            data,
        });
    }

    if (response.error) {
        showToast(response.error.message);
        return;
    }

    runningMessage.value = "Connecting to Stripe...";

    setTimeout(() => {
        if (onBeforeRedirect) {
            onBeforeRedirect();
        }
        // a Back from checkout restores this page stale: reload it then
        markLeavingFor("checkout");
        window.location.href = response.url;
    }, 800);
}
